/**
 * useReservationStayMutation — senyals de la card (`plans/calendar-card-signals.md` §6 B1.2 i F2.1).
 *
 * «Ha entrat», «Ha marxat», «No s'ha presentat» i «Desfer», a mà des del popover del calendari.
 * `PATCH /reservations/:id/stay` només escriu a la DB: no envia res a Beds24 ni revoca codis TTLock.
 */

import { useMutation, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import { api } from '@/services/api';

export type ReservationStayState = 'CHECKED_IN' | 'CHECKED_OUT' | 'NO_SHOW' | 'UNDO';

export interface ReservationStayVariables {
  reservationId: string;
  state: ReservationStayState;
}

/** La crida en si, fora del hook perquè es pugui provar amb `api` mockejat. */
export async function patchReservationStay({ reservationId, state }: ReservationStayVariables) {
  const response = await api.patch(`/reservations/${encodeURIComponent(reservationId)}/stay`, {
    state,
  });
  return response.data;
}

export function useReservationStayMutation() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: patchReservationStay,
    onSuccess: (_data, { reservationId }) => {
      queryClient.invalidateQueries({ queryKey: ['calendar-bookings'], exact: false });
      // «Posar al dia»: el comptador del botó del calendari surt d'aquesta consulta.
      queryClient.invalidateQueries({ queryKey: ['stay-catch-up'] });
      queryClient.invalidateQueries({ queryKey: ['reservation-full', reservationId] });
    },
    onError: (error: unknown) => {
      const detail = error instanceof Error && error.message ? `: ${error.message}` : '';
      toast.error(`No s'ha pogut desar l'entrada o la sortida${detail}`);
    },
  });
}

export default useReservationStayMutation;
